<template>
	<slide-sheet v-model="isCustomShareSheetActive" :title="t('action.share')">
		<div class="flex flex-wrap justify-center py-16 md:px-16">
			<share-sheet-option v-for="option in customShareOptions" :key="option.value" v-bind="option" class="pb-16"/>
		</div>
	</slide-sheet>
</template>

<script setup lang="ts">
import {useI18n, useNuxtApp} from "#imports";
import type {ShareSheetOptionProps} from "./ShareSheetOptionProps";
import {computed, ref, watch} from "vue";
import {mdiFacebook, mdiLinkVariant, mdiSend, mdiWhatsapp} from "@mdi/js";
import {emailIcon} from "../../config";
import type {ShareSheetProps} from "./ShareSheetProps";
import {NativeApp} from "../../services";

const props = defineProps<ShareSheetProps>();

const {$analytics, $notify} = useNuxtApp();
const {t} = useI18n();

const isActive = defineModel({default: false, type: Boolean});
const isCustomShareSheetActive = ref(false);
watch(isActive, (newValue) => {
	if (newValue) {
		openShareSheet()
	}
}, {immediate: true});
watch(isCustomShareSheetActive, (newValue) => {
	isActive.value = newValue;
});

const customShareOptions = computed(() => {
	const options: ShareSheetOptionProps[] = [
		{
			icon: mdiLinkVariant,
			name: t('label.link'),
			value: 'link',
			callback: (option) => {
				navigator.clipboard.writeText(getShareUrl());
				handleSuccessfulCustomShare(option.value);
				$notify('success', t('text.text_copied_to_clipboard'))
			}
		},
		{
			icon: mdiWhatsapp,
			name: 'WhatsApp',
			value: 'whatsapp',
			color: '#25D366',
			callback: (option) => {
				let text = getShareUrl();
				const shareText = getShareText();
				if (shareText) {
					text = `${shareText}\n${text}`
				}

				window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(text)}`, "_blank")
				handleSuccessfulCustomShare(option.value);
			}
		},
		{
			icon: mdiSend,
			name: 'Telegram',
			value: 'telegram',
			color: '#0088cc',
			callback: (option) => {
				let url = `https://telegram.me/share/url?url=${encodeURIComponent(getShareUrl())}`;
				const shareText = getShareText();

				if (shareText) {
					url += `&text=${encodeURIComponent(shareText)}`
				}

				window.open(url, "_blank")
				handleSuccessfulCustomShare(option.value);
			}
		},
		{
			icon: mdiFacebook,
			name: 'Facebook',
			value: 'facebook',
			color: '#3b5998',
			callback: (option) => {
				let url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(getShareUrl())}`;
				const shareText = getShareText();
				if (shareText) {
					url += `&quote=${encodeURIComponent(shareText)}`
				}

				window.open(url, "_blank")
				handleSuccessfulCustomShare(option.value);
			}
		},
		{
			icon: emailIcon,
			name: 'E-Mail',
			value: 'email',
			color: '#465050',
			callback: (option) => {
				let text = getShareUrl();
				const shareText = getShareText();
				if (shareText) {
					text = `${shareText}\n${text}`
				}

				window.open(`mailto:?to=&body=${encodeURIComponent(text)}`, "_blank")
				handleSuccessfulCustomShare(option.value);
			}
		},
	];

	return options;
});

function getShareUrl() {
	return props.url ?? window.location.href;
}

function getShareText() {
	return props.text ?? document.title;
}

async function openShareSheet() {
	const shareUrl = getShareUrl();
	const shareText = getShareText();

	if (NativeApp.isNativeApp()) {
		let text = shareUrl;
		if (shareText) {
			text = `${shareText}\n${text}`
		}

		isActive.value = false;
		await NativeApp.share(text);
		trackShareEvent('native');
		return;
	}

	const shareData = {
		title: 'SPOFERAN',
		text: shareText,
		url: shareUrl,
	}
	if (navigator.canShare && navigator.canShare(shareData)) {
		try {
			isActive.value = false;
			await navigator.share(shareData);
			trackShareEvent('web');
		} catch (e) {
			// When share is cancelled by the user, an "AbortError: About due to cancellation of share" occurs (message differs by browsers)
			// As this is nothing bad and happens quite frequently, we ignore the error
			if (!e?.toString().includes('AbortError')) {
				throw e;
			}
		}
		return;
	}

	isCustomShareSheetActive.value = true;
}

function trackShareEvent(method: string) {
	const [modelName, ...modelKey] = props.id ? props.id.split('-') : [undefined, undefined];
	$analytics.track({
		event: 'share',
		method: method,
		model_type_name: modelName,
		model_key: modelKey.join('-')
	});
}

function handleSuccessfulCustomShare(method: string) {
	trackShareEvent(method);
	isCustomShareSheetActive.value = false;
}

</script>